import React, {useState, useEffect} from 'react';
import styled from "@emotion/styled";
import BtnPrimary from "../../../../../component/PrimaryButton";
import InlineMessage from "../../../../../component/InlineMessage";
import { useNavigate } from "react-router";
import { tokens } from "../../../../../theme";
import Modal, { ModalHead, ModalBody, ModalFoot } from "../../../../../component/Modal";
import BtnSecondary from "../../../../../component/SecondaryButton";
import { useSelfEmployee } from "../../../../../services/useOrgData";
import { amountHint, receiptHint, paymentTiming, blockers } from "./claimRules";
import { approverLine } from "../leave/leaveClash";
import {Avatar, Box,CircularProgress} from '@mui/material';
import FormField from '../../../../../component/Formfield';
import ShareExpense from './ShareExpense';
import DragDrop from '../../../../../component/DragDrop';
import { createExpense, getAllExpenseCategory } from '../../../../../services/expense';
import { getSelfPolicy } from '../../../../../services/policy';
import DateField from '../../../../../component/DateField';
import { getActiveLegalUnit } from '../../../../../services/globalData';
import SuccessDialog from '../../../../../component/SuccessDialog';
import ProgressBar from '../../../../../component/ProgressBar';
import AddDocument from '../../../../../assets/icons/add-document.svg';
import { useNotify } from '../../../../../component/NotificationProvider';

const Label = styled(Box)({
    fontWeight: "500",
    fontSize: "1rem",
    lineHeight:1.7,
    color:"#2A1D22",
    marginBlock: "1rem",
  });


/**
 * Submitting a claim.
 *
 * Rendered as a modal from the claims list, and also reachable at its own route
 * — in which case it opens immediately and closing returns to the list, so an
 * existing link or bookmark still works.
 */
export default function AddNewExpense({ open = true, onClose }) {
  const notify = useNotify();
  const [apiCall,setApiCall]=useState(false);
  const [expenseForm,setExpenseForm] = useState({
    policy:"",
    type:"ANY",
    reason:"",
    amount:"",
    shareWith:null,
    billUrl:null,
    date:null,
    currencyType:""
  })
  const [validationErrors, setValidationErrors] = useState({})
  const [shareExpense,setShareExpense]=useState(false);
  const [startDateDialogOpen, setStartDateDialogOpen] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const navigate = useNavigate();
  const { data: self } = useSelfEmployee();
  const close = () => (onClose ? onClose() : navigate("/app/employee-expense/my-expense"));

  /*
    The three things that decide whether this claim is approved quickly,
    refused, or paid later than expected — all invisible while filling the form
    in. Derived from the chosen category's own limits, which is what the server
    enforces against.
  */
  const category = categoryOptions?.find((c) => c?._id === selectedCategory) || null;
  const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  const amountNote = amountHint(expenseForm.amount, category, money);
  const receiptNote = receiptHint(Boolean(expenseForm.billUrl), category);
  const timing = paymentTiming(expenseForm.expenseDate || expenseForm.date);
  const missing = blockers({
    what: expenseForm.reason,
    category: selectedCategory,
    amount: expenseForm.amount,
    date: expenseForm.expenseDate || expenseForm.date,
  });
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

  useEffect(()=>{
    getPoliciesAndCategories();
  },[]);

  const getPoliciesAndCategories = async () =>{
    try {
    const res = await getSelfPolicy();
    const filteredPolicies = (res?.data?.foundPolicy || []).filter(policy => policy.type === "ANY");

    const policyCategories = [...new Set(filteredPolicies.flatMap(policy => policy.policyCateogary))];

    const policyCategoriesModified = policyCategories.map(category => {
      const matchingObject = filteredPolicies?.find(policy => policy?.policyCateogary?.find(cat => cat._id === category._id));
      if (!category.policy) {
        category.policy = [];
      }
      if (matchingObject) {
        category.policy.push(matchingObject._id);
      }
      return category;
    });

    const legalUnit = getActiveLegalUnit();
    const result = await getAllExpenseCategory(legalUnit?._id);

    const categories = policyCategoriesModified.map(category => {
      const matchingObject = result?.data?.find(data => data._id === category._id);
      if (matchingObject) {
        category.name = matchingObject.name;
        category.value = matchingObject._id;
      }
      return category;
    });

    setCategoryOptions(categories);
    } catch (e) {
      notify.error(e?.message || "Failed to load expense categories");
    }
  }

  const handleChange = (field, value) => {
    setExpenseForm(prevState => ({...prevState, [field]: value}));
    setValidationErrors(prevState => ({...prevState, [field]: ""}));
  };

  const onSubmit = async() => {
    const errors ={};
    if (!expenseForm.reason) {
      errors.reason="Reason is required";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (!selectedCategory) { notify.error("Choose an expense category"); return; }
    try {
      setApiCall(true);
      expenseForm.policyCateogary = selectedCategory;
      expenseForm.policy = categoryOptions?.find(category => category?._id===selectedCategory)?.policy[0];
      const result = await createExpense(expenseForm);

      if(result.success){
        notify.success("Expense submitted");
        setIsSuccessDialogOpen(true);
        setExpenseForm({
          policy:"",
          type:"ANY",
          reason:"",
          amount:"",
          shareWith:null,
          billUrl:null,
          date:null,
          currencyType:""
        });
        setSelectedCategory("");
      } else {
        notify.error(result?.message || "Could not submit expense");
      }
    } catch (error) {
        notify.error(error?.message || "Could not submit expense");
    }
    setApiCall(false);
 };

  /*
    A modal over the claims list, not a page of its own: submitting a claim is
    something you do while looking at your claims, and coming back to the list
    is the normal ending. The route still works — it renders this open, and
    closing goes back — so an existing link or bookmark is not broken.
  */
  return (
    <Modal open={open} onClose={close} width="42.5rem">
      <ModalHead
        title="Submit a claim"
        subtitle="Reimbursed with your salary once approved."
        onClose={close}
      />
      <ModalBody>
              <Box sx={{marginBottom: "2rem", width:"80%"}}>
                          <Box sx={{display:"flex"}}>
                            <Avatar src={AddDocument} style={{ width: '1.5rem', height: '1.5rem' }}/>
                            <Box sx={{fontWeight:400, fontSize:"1rem", lineHeight: 1.7, color:"#8C3B5E", marginBottom: "0.5rem"}}>
                                Add New Expenses
                            </Box>
                          </Box>
                          <ProgressBar value={20}/>                
              </Box>
              <Box sx={{display:"flex", justifyContent:"space-between"}}>
                <Box sx={{width:"48%"}}>
                  <Label>Choose Expense Category</Label>
                  <FormField
                    type="select"
                    options={categoryOptions}
                    value={selectedCategory}
                    fullwidth
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  />  
                </Box>
                <Box sx={{width:"48%"}}>
                  <Label>Select Expense Date</Label>
                  <DateField
                      past={true}
                      value={expenseForm.date}
                      isDialogOpen={startDateDialogOpen}
                      validation={validationErrors.date}
                      fullwidth
                      openDialog={() => setStartDateDialogOpen(true)}
                      closeDialog={() => setStartDateDialogOpen(false)}
                      onSelect={(date) => {
                                handleChange('date', date)
                                setStartDateDialogOpen(false)
                                }}
                  />
                </Box>
              </Box>
              <Label>Reason</Label>
              <FormField
                selectedCategory="text"
                value={expenseForm.reason}
                validation={validationErrors.reason}
                fullwidth
                placeholder="Expense Description (max 100 words)"
                onChange={(e) => handleChange('reason', e.target.value)}
                row={4}
              />  
              <Label>Amount</Label>
              <Box sx={{display:"flex", justifyContent:"space-between"}}>
                  <Box sx={{flex:1, display:"flex", gap: "1.25rem"}}>
                    {/* <FormField
                          selectedCategory="select"
                          width={"20%"}
                          options={currencyList}
                          value={expenseForm.currencyType}
                          onChange={(e) => handleChange('currencyType', e.target.value)}
                    /> */}
                    <FormField
                      selectedCategory="text"
                      value={expenseForm.amount}
                      validation={validationErrors.amount}
                      onChange={(e) => handleChange('amount', e.target.value)}
                    />  
                  </Box>
                  {/* <Button 
                    sx={{ 
                       borderRadius: '12px',
                       fontSize: '0.875rem',
                       lineHeight: '1.31rem',
                       fontWeight:"500",
                       color: '#9A8E84',
                       backgroundColor:'#FBF2EC',
                       textTransform:"none",
                       height:"3.5rem",
                       width:"16.43rem"
                    }}
                    startIcon={<Avatar src={AddExpenseIcon} style={{ width: '1.25rem', height: '1.25rem' }}/>}
                    onClick={()=>setShareExpense(true)}
                  >
                    Share Expense
                  </Button> */}
              </Box>
              {/* The amount rule, live against the chosen category. */}
              {amountNote && (
                <InlineMessage tone={amountNote.tone === "muted" ? "info" : amountNote.tone} sx={{ mb: "1.25rem" }}>
                  {amountNote.text}
                </InlineMessage>
              )}

              <Label>Upload Bill</Label>
              <DragDrop 
               file={expenseForm.billUrl}
               setFile={(e) => handleChange('billUrl', e)}
               style={{
                width:"100%", 
                borderRadius:"12px",
                border:"1px dashed #9A8E84",
                backgroundColor:"#FBF2EC",
                display:"flex",
                flexDirection:"column",
                justifyContent:"center",
                alignItems:"center",
                height:"11.43rem",
                "&:hover":{
                  cursor:"pointer"
                }
              }}/>
              {/* Whether a receipt is needed, said before it is missed. */}
              {receiptNote && (
                <Box sx={{ mt: "0.75rem" }}>
                  <InlineMessage tone={receiptNote.tone === "muted" ? "info" : receiptNote.tone}>
                    {receiptNote.text}
                  </InlineMessage>
                </Box>
              )}

              {/* When the money actually arrives. Reimbursement rides on
                  payroll, and the gap between "a few days" and six weeks is
                  where the complaints come from. */}
              <Box sx={{ mt: "1.25rem" }}>
                <InlineMessage tone={timing.late ? "warning" : "info"}>{timing.text}</InlineMessage>
              </Box>

      </ModalBody>

      <ModalFoot
        note={
          missing.length > 0
            ? `Still needed: ${missing.join(", ")}.`
            : approverLine(self?.reportingManager).replace("You can cancel it while it is pending.", "").trim()
        }
      >
        <BtnSecondary onClick={close} sx={{ borderRadius: "8px" }}>Discard</BtnSecondary>
        <BtnPrimary onClick={onSubmit} disabled={apiCall || missing.length > 0} sx={{ borderRadius: "8px" }}>
          {apiCall ? <CircularProgress size={"1.1rem"} sx={{ color: tokens.onPrimary }} /> : "Submit claim"}
        </BtnPrimary>
      </ModalFoot>

      <ShareExpense open={shareExpense} setOpen={setShareExpense} onClose={()=>setShareExpense(false)} />
          <SuccessDialog 
            open={isSuccessDialogOpen} 
            onClose={()=>setIsSuccessDialogOpen(false)}
            message="Your Expense request has been submitted successfully!"
            addNewLink="/app/employee-expense/add-new" 
            addNewText="Add New Expense"
            viewAllLink="/app/employee-expense/my-expense"
            viewAllText="View All Expenses"
          />
    </Modal>
  );
}
